import { $ } from '../utils/dom.js';
import { escapeHtml } from '../utils/format.js';
import { getConfig, saveConfig, isConfigured } from '../api/appsScript.js';
import { state } from '../state.js';
import {
  getSheetCurrency, setSheetCurrency,
  getRateOverride, setRateOverride,
} from '../currencyStore.js';
import { fetchRates } from '../api/exchangeRate.js';
import { renderRateLabel, CURRENCY_CHANGED_EVENT } from './currencyToggle.js';
import {
  getAdminSession,
  getMetaConfigStatus,
  loginAdmin,
  logoutAdmin,
  testMetaConnection,
} from '../api/metaSales.js';

const localState = {
  recipients: [],
  emailEnabled: true,
  isOpen: false,
  adminAuthenticated: false,
  adminConfigured: null,
  metaConfig: null,
};

function isValidEmail(e) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
}

function renderCurrencyRows() {
  const sheets = state.knownSheets || [];
  if (!sheets.length) {
    return '<span class="meta" style="font-size:12px">Aún no se detectan hojas. Refresca el dashboard.</span>';
  }
  return sheets.map((s) => {
    const cur = getSheetCurrency(s.name);
    return `
      <div class="currency-row">
        <span class="currency-name" title="${escapeHtml(s.name)}">${escapeHtml(s.name)}</span>
        <select class="cur-sheet-select" data-sheet="${escapeHtml(s.name)}">
          <option value="MXN" ${cur === 'MXN' ? 'selected' : ''}>MXN</option>
          <option value="USD" ${cur === 'USD' ? 'selected' : ''}>USD</option>
        </select>
      </div>`;
  }).join('');
}

function renderMetaAdminSection() {
  if (localState.adminConfigured === null) {
    return `<div class="modal-section"><h3>ClicChat y Meta</h3>
      <p class="modal-help">Cargando estado seguro del servidor…</p></div>`;
  }
  if (!localState.adminConfigured) {
    return `<div class="modal-section"><h3>ClicChat y Meta</h3>
      <div class="modal-warning">Configura <code>ADMIN_DASHBOARD_PASSWORD</code> y
      <code>ADMIN_SESSION_SECRET</code> en Vercel para habilitar controles administrativos.</div></div>`;
  }
  if (!localState.adminAuthenticated) {
    return `<div class="modal-section"><h3>ClicChat y Meta</h3>
      <p class="modal-help">Inicia una sesión HttpOnly para consultar payloads, probar Meta y reintentar eventos.</p>
      <div class="recipient-row">
        <input type="password" id="adminPassword" autocomplete="current-password" placeholder="Contraseña administrativa" />
        <button class="btn" id="adminLogin">Entrar</button>
      </div></div>`;
  }
  const cfg = localState.metaConfig;
  if (!cfg) {
    return `<div class="modal-section"><h3>ClicChat y Meta</h3>
      <p class="modal-help">Cargando configuración…</p></div>`;
  }
  const line = (label, value, ok) => `
    <div class="integration-status-row">
      <span>${escapeHtml(label)}</span>
      <span class="pill ${ok ? 'ok' : 'bad'}">${escapeHtml(value)}</span>
    </div>`;
  return `<div class="modal-section">
    <div class="integration-title-row">
      <h3>ClicChat y Meta</h3>
      <button class="btn secondary" id="adminLogout">Cerrar sesión</button>
    </div>
    <div class="integration-status-list">
      ${line('Webhook ClicChat', cfg.clicchat.configured ? 'Configurado' : 'Falta secreto', cfg.clicchat.configured)}
      ${line('API ClicChat', cfg.clicchat.apiEnrichmentConfigured ? 'Enriquecimiento activo' : 'Sin token', cfg.clicchat.apiEnrichmentConfigured)}
      ${line('Base de datos', cfg.database.configured ? 'Conectada' : 'Sin DATABASE_URL', cfg.database.configured)}
      ${line('Meta CAPI', cfg.meta.configured ? 'Configurada' : `Faltan ${cfg.meta.missing.length}`, cfg.meta.configured)}
      ${line('Dataset', cfg.meta.datasetId || 'No configurado', Boolean(cfg.meta.datasetId))}
      ${line('Modo', cfg.meta.testMode ? 'Prueba' : 'Producción', !cfg.meta.testMode)}
      ${line('Inspector', cfg.clicchat.inspectionMode ? 'Activo' : 'Inactivo', true)}
    </div>
    <p class="modal-help">
      Último webhook: ${escapeHtml(cfg.overview?.lastWebhookAt ? new Date(cfg.overview.lastWebhookAt).toLocaleString('es-MX') : '—')}
      · Último envío: ${escapeHtml(cfg.overview?.lastSentAt ? new Date(cfg.overview.lastSentAt).toLocaleString('es-MX') : '—')}
    </p>
    <p class="modal-help">Retención: ${escapeHtml(String(cfg.retentionDays))} días · Productos: ${escapeHtml(String(cfg.products?.length || 0))}</p>
    <button class="btn secondary" id="testMetaConnection" ${cfg.meta.configured ? '' : 'disabled'}>Probar conexión con Meta</button>
    <span class="modal-status" id="metaConnectionStatus"></span>
  </div>`;
}

function render() {
  const root = $('settingsRoot');
  if (!localState.isOpen) {
    root.innerHTML = '';
    return;
  }

  const recipientsHtml = localState.recipients.length
    ? localState.recipients.map((e, i) => `
        <span class="recipient-chip">
          ${escapeHtml(e)}
          <button data-idx="${i}" title="Quitar">×</button>
        </span>
      `).join('')
    : '<span class="meta" style="font-size:12px">Sin destinatarios aún. Agrega al menos uno.</span>';

  const notConfigured = !isConfigured();

  root.innerHTML = `
    <div class="modal-backdrop" id="modalBackdrop">
      <div class="modal" id="modalBox">
        <div class="modal-head">
          <h2>Configuración</h2>
          <button class="modal-close" id="modalClose" title="Cerrar">×</button>
        </div>
        <div class="modal-body">
          <div class="modal-section">
            <h3>Hojas del Sheet</h3>
            <p class="modal-help">
              Las pestañas se detectan automáticamente desde tu Google Sheet.
              Cuando crees, renombres o borres una hoja, refresca el dashboard y
              aparecerá en el dropdown del header.
            </p>
          </div>

          <div class="modal-section">
            <h3>Moneda por producto</h3>
            <p class="modal-help">
              Indica en qué moneda están los pagos de cada hoja. El dashboard
              convertirá automáticamente a la moneda que elijas en el toggle
              <strong>MXN / USD</strong> del header.
            </p>
            <div class="currency-list" id="currencyList">${renderCurrencyRows()}</div>
            <div class="rate-override-row">
              <label for="rateOverride">Tasa USD→MXN manual (opcional)</label>
              <input type="number" id="rateOverride" step="0.01" min="0"
                placeholder="ej. 18.50" value="${getRateOverride() ?? ''}" />
            </div>
            <p class="modal-help">
              Si la dejas vacía, se usa la tasa de cambio en vivo de internet.
            </p>
          </div>

          ${notConfigured ? `
            <div class="modal-warning">
              Las notificaciones por correo aún no están conectadas.<br>
              Define <code>VITE_APPS_SCRIPT_URL</code> y <code>VITE_ADMIN_TOKEN</code> en <code>.env.local</code> y reinicia el servidor.
              Hasta entonces, los campos de abajo son solo previsualización.
            </div>
          ` : ''}

          ${renderMetaAdminSection()}

          <div class="modal-section">
            <h3>Notificaciones por correo</h3>
            <p class="modal-help">
              Se enviará un correo cada vez que una venta tenga STATUS
              <strong>realizado</strong>, <strong>pagado</strong> o <strong>completado</strong>.
            </p>
            <div class="toggle-row">
              <label for="emailEnabledSwitch">Activar envío de correos</label>
              <span class="switch">
                <input type="checkbox" id="emailEnabledSwitch" ${localState.emailEnabled ? 'checked' : ''} />
                <span class="switch-slider"></span>
              </span>
            </div>
          </div>

          <div class="modal-section">
            <h3>Destinatarios</h3>
            <p class="modal-help">
              Usando el remitente de prueba de Resend (<code>onboarding@resend.dev</code>),
              solo llegarán correos a la dirección dueña de la cuenta Resend.
              Para enviar a otros correos, verifica tu dominio en Resend.
            </p>
            <div class="recipient-row">
              <input type="email" id="newRecipient" placeholder="correo@dominio.com" />
              <button class="btn" id="addRecipient">Agregar</button>
            </div>
            <div class="recipients-list" id="recipientsList">${recipientsHtml}</div>
          </div>
        </div>
        <div class="modal-foot">
          <span class="modal-status" id="modalStatus"></span>
          <button class="btn secondary" id="modalCancel">Cancelar</button>
          <button class="btn" id="modalSave" ${notConfigured ? 'disabled' : ''}>Guardar</button>
        </div>
      </div>
    </div>
  `;

  wireEvents();
}

function setStatus(msg, kind) {
  const el = $('modalStatus');
  if (!el) return;
  el.textContent = msg || '';
  el.className = 'modal-status' + (kind ? ' ' + kind : '');
}

function wireEvents() {
  $('modalBackdrop').onclick = (e) => {
    if (e.target.id === 'modalBackdrop') close();
  };
  $('modalClose').onclick = close;
  $('modalCancel').onclick = close;

  // Moneda por producto: persiste al instante y re-renderiza el dashboard.
  document.querySelectorAll('.cur-sheet-select').forEach((sel) => {
    sel.onchange = () => {
      setSheetCurrency(sel.dataset.sheet, sel.value);
      window.dispatchEvent(new Event(CURRENCY_CHANGED_EVENT));
    };
  });

  // Tasa manual: al cambiar, re-resuelve tasas (el override gana) y re-renderiza.
  const rateInput = $('rateOverride');
  if (rateInput) {
    rateInput.onchange = async () => {
      setRateOverride(rateInput.value);
      await fetchRates();
      renderRateLabel();
      window.dispatchEvent(new Event(CURRENCY_CHANGED_EVENT));
    };
  }

  $('emailEnabledSwitch').onchange = (e) => {
    localState.emailEnabled = e.target.checked;
  };

  $('addRecipient').onclick = addCurrent;
  $('newRecipient').onkeydown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCurrent();
    }
  };

  $('recipientsList').querySelectorAll('button[data-idx]').forEach((btn) => {
    btn.onclick = () => {
      const i = +btn.dataset.idx;
      localState.recipients.splice(i, 1);
      renderList();
    };
  });

  $('modalSave').onclick = handleSave;

  const adminLogin = $('adminLogin');
  if (adminLogin) adminLogin.onclick = handleAdminLogin;
  const adminPassword = $('adminPassword');
  if (adminPassword) {
    adminPassword.onkeydown = (event) => {
      if (event.key === 'Enter') handleAdminLogin();
    };
  }
  const adminLogout = $('adminLogout');
  if (adminLogout) adminLogout.onclick = handleAdminLogout;
  const metaTest = $('testMetaConnection');
  if (metaTest) metaTest.onclick = handleMetaConnectionTest;
}

async function handleAdminLogin() {
  const input = $('adminPassword');
  const button = $('adminLogin');
  if (!input?.value) return;
  button.disabled = true;
  try {
    await loginAdmin(input.value);
    localState.adminAuthenticated = true;
    localState.metaConfig = await getMetaConfigStatus();
    render();
  } catch {
    setStatus('Contraseña administrativa incorrecta.', 'bad');
    button.disabled = false;
  }
}

async function handleAdminLogout() {
  await logoutAdmin();
  localState.adminAuthenticated = false;
  localState.metaConfig = null;
  render();
}

async function handleMetaConnectionTest() {
  const button = $('testMetaConnection');
  const status = $('metaConnectionStatus');
  button.disabled = true;
  status.textContent = 'Probando…';
  try {
    const result = await testMetaConnection();
    status.textContent = result.ok
      ? 'Conexión correcta: Meta devolvió HTTP ' + result.httpStatus
      : 'Meta rechazó la conexión.';
    status.className = 'modal-status ' + (result.ok ? 'ok' : 'bad');
  } catch (error) {
    status.textContent = 'Error: ' + error.message;
    status.className = 'modal-status bad';
  } finally {
    button.disabled = false;
  }
}

function addCurrent() {
  const input = $('newRecipient');
  const v = input.value.trim().toLowerCase();
  if (!v) return;
  if (!isValidEmail(v)) {
    setStatus('Correo inválido', 'bad');
    return;
  }
  if (localState.recipients.includes(v)) {
    setStatus('Ese correo ya está en la lista', 'bad');
    return;
  }
  localState.recipients.push(v);
  input.value = '';
  setStatus('');
  renderList();
}

function renderList() {
  const cont = $('recipientsList');
  if (!cont) return;
  cont.innerHTML = localState.recipients.length
    ? localState.recipients.map((e, i) => `
        <span class="recipient-chip">
          ${escapeHtml(e)}
          <button data-idx="${i}" title="Quitar">×</button>
        </span>
      `).join('')
    : '<span class="meta" style="font-size:12px">Sin destinatarios aún. Agrega al menos uno.</span>';
  cont.querySelectorAll('button[data-idx]').forEach((btn) => {
    btn.onclick = () => {
      const i = +btn.dataset.idx;
      localState.recipients.splice(i, 1);
      renderList();
    };
  });
}

async function handleSave() {
  const btn = $('modalSave');
  btn.disabled = true;
  setStatus('Guardando…');
  try {
    await saveConfig({
      recipients: localState.recipients,
      emailEnabled: localState.emailEnabled,
    });
    setStatus('Guardado ✓', 'ok');
    setTimeout(close, 700);
  } catch (e) {
    setStatus('Error: ' + e.message, 'bad');
    btn.disabled = false;
  }
}

function close() {
  localState.isOpen = false;
  render();
}

export async function openSettingsModal() {
  localState.isOpen = true;
  render();

  setStatus('Cargando configuración…');
  const tasks = [
    getAdminSession()
      .then(async (session) => {
        localState.adminConfigured = session.configured;
        localState.adminAuthenticated = session.authenticated;
        localState.metaConfig = session.authenticated ? await getMetaConfigStatus() : null;
      })
      .catch(() => {
        localState.adminConfigured = false;
        localState.adminAuthenticated = false;
        localState.metaConfig = null;
      }),
  ];
  if (isConfigured()) {
    tasks.push(
      getConfig().then((cfg) => {
        localState.recipients = Array.isArray(cfg.recipients) ? cfg.recipients : [];
        localState.emailEnabled = cfg.emailEnabled !== false;
      }),
    );
  }
  try {
    await Promise.all(tasks);
    setStatus('');
    render();
  } catch (e) {
    setStatus('Error: ' + e.message, 'bad');
  }
}

export function bindSettingsButton() {
  const btn = $('settingsBtn');
  if (btn) btn.onclick = openSettingsModal;
}
